import { type BTSelectItem } from "types/apiResponse/apiResponse";

import {
    EditorContent,
    type IEditorApiType,
} from "commonComponents/btWrappers/editor/editor.types";

import { type IApprovalUserExtraData } from "../ApprovalUserSelect/ApprovalUserSelect.type";

export interface IApprovalViewerProps {
    /**
     * Approvals from API response
     */
    approvals: IApprovalInfo[];

    /**
     * Optional IDs from form state not yet saved (pending approvers)
     * These will be merged with API approvals for display
     */
    pendingApproverIds?: number[];

    /**
     * Optional additional CSS class names
     */
    className?: string;

    /**
     * Test ID for testing purposes
     */
    "data-testid"?: string;

    /**
     * Callback invoked when an approver is marked for deletion
     * Parent should update form state to remove this user ID
     * @param userId The global user ID of the approver to delete
     */
    onApproverDelete?: (userId: number, step: number) => void;

    /**
     * Callback invoked when an approver's status is marked for reset to Pending
     * Parent should update form state to track this user ID for reset on save
     * @param userId The global user ID of the approver to reset
     */
    onApproverReset?: (userId?: number) => void;

    /**
     * Controls whether delete buttons are shown for approvers
     * Defaults to false for safety (delete disabled by default)
     */
    canDelete?: boolean;

    /**
     * Callback invoked when new approvers are added via inline UI
     * @param userIds Array of global user IDs to add as approvers
     */
    onAddApprover?: (userIds: number[]) => void;

    /**
     * Function to fetch approval users for the dropdown
     * Returns BTSelectItem array with IApprovalUserExtraData
     */
    getApprovalUsers: () => Promise<BTSelectItem<IApprovalUserExtraData>[]>;

    /**
     * Controlled state: whether the inline add mode is active
     * Parent component controls this state
     */
    isAddingApprover?: boolean;

    /**
     * Callback invoked when canceling the add approver flow
     */
    onCancelAdding?: () => void;

    /**
     * The current active approval step (lowest step with non-Approved status).
     * When provided, only approvers on this step can be reset in sequential mode.
     * Null means the review process is not active (before send or after ready for payment).
     */
    currentApprovalStep?: number | null;
}

export enum ApprovalStatus {
    NotSent = 0,
    Pending = 1,
    Approved = 2,
    Rejected = 3,
    NotNeeded = 4,
}

export enum ApprovalMode {
    AllMustApprove = 0,
    AnyOneApproves = 1,
}

/**
 * Sort rank for approver display order (lower first): Rejected, Approved, Pending, NotSent, NotNeeded.
 * Not the raw enum value — NotNeeded (4) would otherwise sort to the top.
 */
export function approvalStatusSortRank(status: ApprovalStatus): number {
    switch (status) {
        case ApprovalStatus.Rejected:
            return 0;
        case ApprovalStatus.Approved:
            return 1;
        case ApprovalStatus.Pending:
            return 2;
        case ApprovalStatus.NotSent:
            return 3;
        case ApprovalStatus.NotNeeded:
            return 4;
        default:
            return 5;
    }
}

/** Shown as an approver's name while their user record is still loading. */
export const LOADING_APPROVER_NAME = "Loading...";

/**
 * An approver's name from the users loaded so far. If their user record hasn't loaded yet, the
 * name is the loading placeholder. `isNameLoading` is always set, so spreading the result over a
 * stored approver also clears an old loading flag.
 */
export function resolveApproverName(
    loadedUsers: BTSelectItem<IApprovalUserExtraData>[],
    userId: number,
): Pick<IApprovalInfo, "approverName" | "isNameLoading"> {
    const user = loadedUsers.find((u) => parseInt(u.id, 10) === userId);
    const name = user?.extraData?.displayName || user?.title;
    return name
        ? { approverName: name, isNameLoading: false }
        : { approverName: LOADING_APPROVER_NAME, isNameLoading: true };
}

/**
 * The accessible name for an approver row's "More" menu button. With no real name yet it stays
 * plain "More", so screen readers never hear "More actions for Loading...". When there is more
 * than one level, the level is added, since one person can be an approver on several levels.
 */
export function approverMenuLabel(
    approver: Pick<IApprovalInfo, "approverName" | "isNameLoading" | "step">,
    levelCount: number,
): string {
    const name = approver.isNameLoading ? "" : approver.approverName.trim();
    if (!name) {
        return "More";
    }
    const level = levelCount > 1 ? `, level ${approver.step}` : "";
    return `More actions for ${name}${level}`;
}

export interface IApprovalInfo {
    approvalId: number;
    globalUserId: number;
    status: ApprovalStatus;
    statusDate: Date;
    approverName: string;
    /** True while the approver's user record hasn't loaded, so `approverName` is a placeholder. */
    isNameLoading?: boolean;
    approvalCommentData?: IApprovalCommentData[];
    step: number;
    mode: ApprovalMode;
}

interface IApprovalCommentData {
    commentText?: string;
    commentJson?: EditorContent;
}

export interface IApprovalServerResponse
    extends Omit<IApprovalInfo, "statusDate" | "approvalCommentData"> {
    statusDate: string;
    approvalCommentData?: { commentText?: string; commentJson?: string }[];
}

export class ApprovalInfo implements IApprovalInfo {
    constructor(data: IApprovalServerResponse) {
        this.approvalId = data.approvalId;
        this.globalUserId = data.globalUserId;
        this.status = data.status;
        this.statusDate = new Date(data.statusDate);
        this.approverName = data.approverName;
        this.step = data.step ?? 1;
        this.mode = data.mode ?? ApprovalMode.AllMustApprove;

        if (data.approvalCommentData?.length) {
            this.approvalCommentData = data.approvalCommentData.map((comment) => {
                let commentJson: EditorContent | undefined;
                try {
                    const parsed = JSON.parse(comment.commentJson ?? "") as IEditorApiType;
                    const editorContent = new EditorContent(parsed);
                    commentJson = editorContent.content ? editorContent : undefined;
                } catch {
                    commentJson = undefined;
                }
                return { commentText: comment.commentText, commentJson };
            });
        }
    }

    approvalId: number;
    globalUserId: number;
    status: ApprovalStatus;
    statusDate: Date;
    approverName: string;
    step: number;
    mode: ApprovalMode;
    approvalCommentData?: IApprovalCommentData[];
}

export interface IApprovalUserRequest {
    globalUserId: number;
    step: number;
    mode: ApprovalMode;
}

export class AddApprovalRequest implements IApprovalUserRequest {
    constructor(data: IApprovalUserRequest) {
        this.globalUserId = data.globalUserId;
        this.step = data.step ?? 1;
        this.mode = data.mode ?? ApprovalMode.AllMustApprove;
    }
    globalUserId: number;
    step: number;
    mode: ApprovalMode;
}
